define('modules/editor/models/editorstate',['require','exports','module','backbone','common/models/appstate','./facebookapi','./linkedinapi','./movablestate','./groupstate','./activeitemstate','modules/lp_integration/models/integration_status'],function (require, exports, module) {
  "use strict";
  var Backbone = require("backbone"),
    AppState = require("common/models/appstate"),
    FacebookAPI = require("./facebookapi"),
    LinkedInAPI = require("./linkedinapi"),
    MovableState = require("./movablestate"),
    GroupState = require("./groupstate"),
    ActiveItemState = require("./activeitemstate"),
    ActiveIntegrations = require("modules/lp_integration/models/integration_status");

  /**
   Represents the editor view state, a simple facade
   to the individual state components
   **/
  var EditorState = Backbone.Model.extend({
    //sort of constants for event names, better decoupling
    MODAL_OPEN: "change:modalOpen",
    VARIANT_CHANGED: "change:variantChanged",
    SELECTED_ELEMENT_CHANGE: "change:selectedElement",
    GROUP_CHANGE: "change:group",
    ACTIVE_PAGE_CHANGE: "change:activePage",
    CURRENT_STEP_CHANGE: "change:currentStep",

    defaults: {
      variant: null,
      modalOpen: false,
      selectedElement: null,
      displayMargins: true,
      displayTree: false,
      currentStep: 0,
      //used in HTML element, only spit out content in page generation
      renderfull: false,
      isAdvancedMode: false
    },

    initialize: function () {
      this.groupState = new GroupState();
      this.moveState = new MovableState();
      this.activeItemState = new ActiveItemState();
      this.fbAPI = new FacebookAPI();
      this.linkedinAPI = new LinkedInAPI();
      this.activeIntegrations = new ActiveIntegrations({}, {});
      this.listenTo(this.groupState, "change:group", function (groupState, groupItems) {
        this.trigger("change:group", groupState, groupItems);
      });
      this.listenTo(this, "change:variant", this.setActiveIntegrations);
    },

    /**
     * Marks the state of the page to be in editing or generation mode
     * In generation mode, we render every content. In editor mode
     * some content can't be rendered (example: HTML element, html_content pg)
     * @param {bool} isGenerating - True if we are generating HTML content
     **/
    setGenerateMode: function (isGenerating) {
      this.set("renderfull", isGenerating);
    },

    /**
     * Returns the current generation state - see previous function
     **/
    isGenerateMode: function () {
      return this.get("renderfull");
    },

    /**
     * Holds a reference to the currently edited LP variant
     * @param {Variant} variant - Variant model
     */
    setVariant: function (variant) {
      this.set("variant", variant);
    },

    /**
     * Returns a reference to the currenly edited LP variant
     * @return {Variant}         Variant model
     */
    getVariant: function () {
      return this.get("variant");
    },

    /** Helper function to get unique control ids
     * @return {integer} New numeric id for element
     **/
    getUniqueId: function () {
      if (!this.get("lastId") && this.has("variant")) {
        this.set("lastId", this.get("variant").getElementCount() + 1);
      }
      var lastId = this.get("lastId");
      this.set("lastId", lastId + 1);
      return lastId;
    },

    setUniqueId: function (id) {
      this.set("lastId", id + 1, {silent: true});
    },

    /**
     * Sets if current editor view is in AMP Mode
     * @param {boolean} state
     */
    setAMPState: function (state) {
      this.set('isAMP', state);
    },

    /**
     * Get if current editor instance is in AMP Mode
     * @return {boolean}
     */
    getAMPState: function () {
      return this.get('isAMP');
    },

    isCurrentStepPopup: function () {
      return this.get('activePage').get('role') === 'popup';
    },

    /**
     * Sets if there are any modal windows
     * @param {boolean} isOpen
     */
    setModalOpen: function (isOpen) {
      this.set("modalOpen", isOpen);
    },

    /** Selects an element VIEW
     * @param {View} view - An element's view
     */
    selectElement: function (view) {
      this.set("selectedElement", view);
      if (view && view.model) {
        this.activeItemState.selectItem(view.model);
      }
    },

    /**
     Deselects any element VIEW
     */
    deselect: function () {
      this.unset("selectedElement");
      this.activeItemState.deselect();
    },
    /**
     * Returns the currently selected element's VIEW
     * @return {View}
     */
    getSelectedElement: function () {
      return this.get("selectedElement");
    },

    /**
     * Sets if the user has made any changes to the variant
     * @param {Boolean} isChanged
     */
    setVariantChanged: function (isChanged, isTrackable) {
      this.set("variantChanged", isChanged);
      AppState.toggleNavigation(!isChanged);

      if (isChanged && isTrackable) this.trigger("variant:changed");
    },

    /**
     * Returns whether the user has made any unsaved changes
     * @return {boolean}
     */
    getVariantChanged: function () {
      return this.get("variantChanged");
    },

    /**
     * Stores the current active page model (content / confirmation)
     * @param {Page} pageModel - The current visible page model
     */
    setActivePage: function (pageModel) {
      this.set("activePage", pageModel);
    },

    /**
     * Returns the current active page model
     * @return {Page}
     */
    getActivePage: function () {
      return this.get("activePage");
    },

    setFBAppId: function (id) {
      this.fbAPI.set("appId", id);
    },

    setLinkedInAppId: function (id) {
      this.linkedinAPI.set("appId", id);
    },

    /**
     * Sets active integrations once variant has been loaded in EditorState
     */
    setActiveIntegrations: function (nop, variant) {
      this.activeIntegrations.pageId = variant.get("pages_id");
      this.activeIntegrations.fetch();
    },
    /**
     * Returns current active integrations
     */
    getActiveIntegrations: function () {
      return this.activeIntegrations.attributes;
    },

    setCurrentStep: function (step, trigger) {
      this.set("currentStep", step || 0, {silent: !!trigger});
      // trigger when asked, even if old and new values are equal
      if (trigger) this.trigger("change:currentStep", this, step);
    },

    getCurrentStep: function () {
      return this.get("currentStep");
    },

    getCurrentStepId: function () {
      return this.get('activePage').get('id');
    },

    getCurrentStepNumber: function () {
      return this.get('activePage').get('step');
    }
  });

  //Singleton usage
  module.exports = new EditorState();
  //NOTE: debug
  window.__lpgEditorState = module.exports;
});

